import React, { FC, useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { User } from "src/api/types";
import GlobalContext from "src/context/global-context";
import { APISomethingWrongMsg, NetworkDisconnectMsg } from "src/lib/constants";
import { getErrorMessage, logger } from "src/lib/utils";
import client from "../../api";

type Props = {
  user: User;
};

/* ---------------- TYPES ---------------- */

interface QuestionnaireItem {
  title: string;
  answered: boolean;
  answer: string | null;
  answers: string[] | null;
  label: string | null;
  labels: string[] | null;
  score: number | null;
}

interface QuestionnaireSection {
  title: string;
  items: QuestionnaireItem[];
}

interface QuestionnaireApiResponse {
  status: string;
  code: number;
  message: string;
  data: unknown;
  items: QuestionnaireSection[];
  totalcount: number;
  page: number;
  totalPages: number | null;
}

const UserQuestionnaireTab: FC<Props> = ({ user }) => {
  const [dataSource, setDataSource] = useState<QuestionnaireSection[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);

  /* ---------------- GET QUESTIONNAIRE ---------------- */

  const { mutate: getQuestionnaire, isLoading } = useMutation(
    client.newUserKycInfo.getKycInfoQuestionnaire,
    {
      onSuccess: (data: QuestionnaireApiResponse) => {
        setDataSource(data?.items || []);
        setTotalCount(data?.totalcount || 0);
        if (data?.message === "You do not have access for this route!") {
          setAlertText(data?.message);
          setIsAlertOpened(true);
        }
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else if (error.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
        setIsAlertOpened(true);
      },
    },
  );

  useEffect(() => {
    if (user?.id) getQuestionnaire({ id: user.id });
  }, [user?.id]);

  /* ---------------- HELPERS ---------------- */

  const renderAnswer = (item: QuestionnaireItem) => {
    if (!item?.answered) return "N/A";

    if (item?.answer) {
      return `${item.answer} ${item?.label ? " : " + item.label : ""}`;
    }

    if (item?.answers && item?.answers.length > 0) {
      return item.answers.map((ans, index) => (
        <div key={index}>
          {ans}
          {item.labels?.[index] ? ` : ${item.labels[index]}` : ""}
        </div>
      ));
    }

    return "N/A";
  };

  /* ---------------- UI ---------------- */

  if (isLoading) return <div className="flex justify-center items-center h-40 text-gray-400">Loading questionnaire...</div>;
  if (!dataSource.length) {
return (
  <div className="flex justify-center items-center h-40 text-gray-400">
    No questionnaire data found
  </div>
);  }
  return (
    <div className="space-y-6">
      {dataSource.map((section, sectionIndex) => (
        <div
          key={sectionIndex}
          className="border rounded-xl p-4 shadow-sm bg-white"
        >
          {/* Section Title */}
          <h3 className="text-lg font-semibold mb-4">{section.title}</h3>

          {/* Questions */}
          <div className="space-y-3">
            {section.items.map((item, index) => (
              <div
                key={index}
                className="flex flex-col border-b pb-2 last:border-none"
              >
                {item.title && (
                  <span className="text-sm font-medium text-gray-700">
                    {item.title}
                  </span>
                )}

                <span className="text-sm text-gray-500">
                  {renderAnswer(item)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default UserQuestionnaireTab;
